import { Integration } from '@sentry/core';
import {
  init,
  browserTracingIntegration,
  breadcrumbsIntegration,
  replayIntegration,
  replayCanvasIntegration,
  httpClientIntegration,
  TraceService,
  createErrorHandler,
  ErrorEvent,
  addBreadcrumb,
} from '@sentry/angular';
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideDamoAuthRoutes, provideDamoAuth } from '@damotech/damoauth-lib';
import { environment } from '@app/environments/environment';

function isHttpStatusBanned(statusCode: number) {
  if (statusCode === 401) {
    addBreadcrumb({ message: '401 banned to silence oobCode expiration error', level: 'info' });
    return true;
  }
  return false;
}

function getIntegrations(): Integration[] {
  const integrations: Integration[] = [browserTracingIntegration()];

  if (environment.name !== 'production') {
    integrations.push(breadcrumbsIntegration({ console: false }));
  }
  integrations.push(replayIntegration({ maskAllText: true }));
  if (environment.target !== 'app') {
    integrations.push(replayCanvasIntegration());
  }

  integrations.push(
    httpClientIntegration({
      failedRequestStatusCodes: [[400, 599]],
    }),
  );
  return integrations;
}

export function initSentry() {
  const integrations = getIntegrations();

  init({
    dsn: environment.sentry.dsn,
    environment: environment.name,
    release: environment.sentry.release,
    integrations,
    replaysSessionSampleRate: 1,
    tracesSampleRate: 0.01,
    replaysOnErrorSampleRate: 1,
    tracePropagationTargets: [environment.fns],
    normalizeDepth: 10,
    beforeSend(event: ErrorEvent) {
      const statusCode = event.contexts?.response?.status_code;
      if (isHttpStatusBanned(statusCode)) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb, _hint) {
      if (environment.name !== 'production') {
        console.log(`[breadcrumb] ${breadcrumb.category ?? ''} ${breadcrumb.message ?? ''}`);
      }
      return breadcrumb;
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ErrorHandler, useValue: createErrorHandler() },
    TraceService,
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideDamoAuth({
      firebase: environment.firebase,
      fns: environment.fns,
      fnsOrigin: environment.fnsOrigin,
      domain: environment.domain,
      postLoginRedirectUrl: environment.postLoginRedirectUrl,
    }),
    provideDamoAuthRoutes(),
    provideTranslateService({ fallbackLang: 'en', extend: true }),
  ],
};
